import { Add, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { FC, ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { createActivity } from "../../api/activity";
import { getManagedProjects } from "../../api/app";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { CreateActivityRequest } from "../../types/model/activity";
import { Project } from "../../types/model/project";
import { StoreInterfaces } from "../../types/store";
import ProjectQuickCreateDialog from "../Projects/ProjectQuickCreateDialog";

interface ActivityWizardDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ActivityFormValues {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
}

const ActivityWizardDialog: FC<ActivityWizardDialogProps> = ({
  open,
  onClose,
  onSuccess,
}): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formStyles = useFormStyles();
  const session = useSelector((state: StoreInterfaces) => state.session);


  const steps = [
    t("activities.wizard.select-project"),
    t("activities.wizard.details"),
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectCreateOpen, setProjectCreateOpen] = useState(false);
  const [activitySubmitting, setActivitySubmitting] = useState(false);

  useEffect(() => {
    if (open && activeStep === 0) {
      fetchProjects();
    }
  }, [open, activeStep]);

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const data = await getManagedProjects();
      setProjects(data);
      return data;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setActiveStep(1);
  };

  const handleProjectCreate = async (projectId: number) => {
    const data = await fetchProjects();
    const created = data.find((p) => p.id === projectId);
    if (created) handleProjectSelect(created);
    setProjectCreateOpen(false);
  };

  const handleActivitySubmit = async (values: ActivityFormValues) => {
    if (!selectedProject) {
      enqueueSnackbar(t("generic.error"), { variant: "error" });
      return;
    }

    setActivitySubmitting(true);
    dispatch(setLoading(true));

    try {
      const request: CreateActivityRequest = {
        name: values.name,
        description: values.description,
        ...(values.startDate ? { startDate: moment(values.startDate).toDate() } : {}),
        ...(values.endDate ? { endDate: moment(values.endDate).toDate() } : {}),
        managerId: session?.userId || 0,
        accountId: session?.accountId || 0,
        ownerId: session?.userId || 0,
      };

      const response = await createActivity(request, selectedProject.id);

      const activityId = response?.id;
      if (!activityId) return;

      enqueueSnackbar(t("activities.created"), {
        variant: "success",
      });

      const accountId = session?.accountId;
      if (!accountId) return;

      navigate(
        `/accounts/${accountId}/projects/${selectedProject.id}/activities/${activityId}`
      );

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setActivitySubmitting(false);
      dispatch(setLoading(false));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedProject(null);
    setProjects([]);
    setProjectSearch("");
    onClose();
  };

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("activities.create")} - {steps[activeStep]}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stepper 
            activeStep={activeStep}
            sx={{
              mb: 3,
              "& .MuiStepIcon-root.Mui-completed": {
                color: "secondary.main",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* STEP 0 - Project */}
          {activeStep === 0 && (
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder={t("generic.search")}
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mb: 2 }}
              />

              {projectsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: "auto", mb: 2 }}>
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <Card
                        key={project.id}
                        sx={{
                          m: 1.5,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": { boxShadow: 2 },
                        }}  
                      >
                        <CardActionArea onClick={() => handleProjectSelect(project)}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {project.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                              {project.description}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ))
                  ) : (
                    <Typography align="center">{t("generic.no-data")}</Typography>
                  )}
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 1,
                  px: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t("projects.not-found-description")}
                </Typography>

                <Button
                  size="small"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={() => setProjectCreateOpen(true)}
                >
                  {t("projects.create")}
                </Button>
              </Box>
            </Box>
          )}

          {/* STEP 1 - Details */}
          {activeStep === 1 && selectedProject && (
            <Formik<ActivityFormValues>
              initialValues={{ 
                name: "",
                description: "",
                startDate: "",
                endDate: ""
              }}
              validationSchema={Yup.object().shape({
                name: Yup.string().required(t("activities.name-required")),
                description: Yup.string().required(t("activities.description-required")),
              })}
              onSubmit={handleActivitySubmit}
            >
              {({ values, errors, touched, handleSubmit, handleBlur, setFieldValue }) => (
                <Box component="form" id="activity-form" onSubmit={handleSubmit}>
                  <Grid2 container spacing={1} sx={{ mb: 3 }}>
                    <Grid2 size={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t("generic.project")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedProject.name}
                      </Typography>
                    </Grid2>
                  </Grid2>

                  <Grid2 container spacing={2}>
                    <Grid2 size={12}>
                      <TextField
                        fullWidth
                        required
                        name="name"
                        label={t("generic.name")}
                        value={values.name}
                        onChange={(e) => setFieldValue("name", e.target.value)}
                        onBlur={handleBlur}
                        error={!!(errors.name && touched.name)}
                        helperText={touched.name && errors.name}
                        sx={{ ...formStyles }}
                      />
                    </Grid2>

                    <Grid2 size={12}>
                      <TextField
                        fullWidth
                        required
                        name="description"
                        label={t("generic.description")}
                        multiline
                        rows={3}
                        value={values.description}
                        onChange={(e) => setFieldValue("description", e.target.value)}
                        onBlur={handleBlur}
                        error={!!(errors.description && touched.description)}
                        helperText={touched.description && errors.description}
                        sx={{ ...formStyles }}
                      />
                    </Grid2>

                    <Grid2 size={12}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600, mb: 1, mt: 1.5 }}>
                        {t("projects.dates")} ({t("generic.optional")})
                      </Typography>

                      <Grid2 container spacing={2}>
                        <Grid2 size={6}>
                          <DatePicker
                            label={t("activities.start-date")}
                            value={values.startDate ? moment(values.startDate) : null}
                            onChange={(date) => setFieldValue("startDate", date ? date.toISOString() : "")}
                            slotProps={{
                              field: { clearable: true },
                              textField: {
                                fullWidth: true,
                                error: !!(errors.startDate && touched.startDate),
                                helperText: touched.startDate && errors.startDate ? errors.startDate : "",
                                sx: { ...formStyles },
                              },
                            }}
                            format="DD/MM/YYYY"
                            views={["day", "month", "year"]}
                          />
                        </Grid2>

                        <Grid2 size={6}>
                          <DatePicker
                            label={t("activities.end-date")}
                            value={values.endDate ? moment(values.endDate) : null}
                            onChange={(date) => setFieldValue("endDate", date ? date.toISOString() : "")}
                            slotProps={{
                              field: { clearable: true },
                              textField: {
                                fullWidth: true,
                                error: !!(errors.endDate && touched.endDate),
                                helperText: touched.endDate && errors.endDate ? errors.endDate : "",
                                sx: { ...formStyles },
                              },
                            }}
                            format="DD/MM/YYYY"
                            views={["day", "month", "year"]}
                          />
                        </Grid2>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Box>
              )}
            </Formik>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button onClick={handleClose} color="primary">
            {t("generic.cancel")}
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            {activeStep > 0 && (
              <Button onClick={handleBack} variant="outlined" color="secondary">
                {t("generic.back")}
              </Button>
            )}

            {activeStep === steps.length - 1 && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  const form = document.getElementById("activity-form") as HTMLFormElement | null;
                  form?.requestSubmit();
                }}
                disabled={activitySubmitting}
              >
                {activitySubmitting ? <CircularProgress size={18} color="inherit" /> : t("generic.create")}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <ProjectQuickCreateDialog
        open={projectCreateOpen}
        onClose={() => setProjectCreateOpen(false)}
        onSuccess={handleProjectCreate}
      />
    </>
  );
};

export default ActivityWizardDialog;